/**
 * SPH Grid Shaders - Spatial Hash Grid with Counting Sort
 *
 * Implements O(n) spatial hashing for efficient neighbor search in SPH simulations.
 * Uses a 5-pass counting sort algorithm:
 * 1. Hash particles to grid cells
 * 2. Clear cell counters
 * 3. Count particles per cell (atomic)
 * 4. Exclusive prefix sum (parallel scan)
 * 5. Scatter particles into sorted array
 *
 * Grid dimensions are typically 32^3 to 128^3 cells.
 * Supports up to ~2M grid cells with single-level prefix sum.
 */

import { SPH_STRUCT_TANIMLARI, SPH_GRID_YARDIMCI, buildShader } from './sph-ortak-shaders.wgsl';

/**
 * Params + particle bindings (group 0/1) needed by grid shaders
 */
const GRID_PARAMS_BINDINGS = /* wgsl */`
@group(0) @binding(0) var<uniform> params: SPHParams;
@group(1) @binding(0) var<storage, read_write> positionMass: array<vec4<f32>>;
@group(1) @binding(1) var<storage, read_write> velocityAge: array<vec4<f32>>;
@group(1) @binding(2) var<storage, read_write> densityMisc: array<vec4<f32>>;
@group(1) @binding(3) var<storage, read_write> forceVmax: array<vec4<f32>>;
@group(1) @binding(4) var<storage, read_write> atomicsBuffer: array<u32>;
@group(1) @binding(5) var<storage, read_write> heatmapBuffer: array<f32>;
`;

/**
 * Grid buffer bindings (group 2)
 * These complement the common SPH buffers in group 0/1
 */
const GRID_BUFFER_BINDINGS = /* wgsl */`
@group(2) @binding(0) var<storage, read_write> cellCount: array<atomic<u32>>;
@group(2) @binding(1) var<storage, read_write> prefixSum: array<u32>;
@group(2) @binding(2) var<storage, read_write> sortedIndex: array<u32>;
@group(2) @binding(3) var<storage, read_write> cellHashBuffer: array<u32>;
@group(2) @binding(4) var<storage, read_write> cellOffset: array<atomic<u32>>;
@group(2) @binding(5) var<storage, read_write> blockSums: array<u32>;
`;

/**
 * PASS 1: Hash Particles - Assign each particle to a grid cell
 * Computes spatial hash and stores in cellHashBuffer
 */
export const GRID_HASH_PARTICLES = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${SPH_GRID_YARDIMCI}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn hashParticles(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.activeCount) { return; }

  // Get particle position
  let pos = positionMass[idx].xyz;

  // Convert to grid cell coordinates
  let gridOrigin = vec3<f32>(
    params.gridOrigin_x,
    params.gridOrigin_y,
    params.gridOrigin_z
  );
  let gridDim = vec3<u32>(
    params.gridDim_x,
    params.gridDim_y,
    params.gridDim_z
  );

  let cell = posToCell(pos, gridOrigin, params.gridCellSize);

  // Compute spatial hash
  let hash = cellHash(cell, gridDim);

  // Store hash for this particle
  cellHashBuffer[idx] = hash;
}
`;

/**
 * PASS 2: Clear Counts - Zero out cell particle counters
 */
export const GRID_CLEAR_COUNTS = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn clearCounts(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.totalGridCells) { return; }

  // Reset atomic counter to zero
  atomicStore(&cellCount[idx], 0u);
}
`;

/**
 * PASS 3: Count Particles - Count particles per cell using atomic increment
 */
export const GRID_COUNT_PARTICLES = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn countParticles(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.activeCount) { return; }

  // Get this particle's cell hash
  let hash = cellHashBuffer[idx];

  // Atomically increment cell counter
  atomicAdd(&cellCount[hash], 1u);
}
`;

/**
 * PASS 4a: Prefix Sum Local - Parallel prefix sum within workgroups
 * Uses Blelloch scan algorithm with shared memory
 * Each workgroup processes 512 elements (2 per thread)
 */
export const GRID_PREFIX_SUM_LOCAL = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

// Shared memory for parallel scan
var<workgroup> temp: array<u32, 512>;

@compute @workgroup_size(256)
fn prefixSumLocal(
  @builtin(global_invocation_id) gid: vec3<u32>,
  @builtin(local_invocation_id) lid: vec3<u32>,
  @builtin(workgroup_id) wid: vec3<u32>
) {
  let globalIdx = gid.x;
  let localIdx = lid.x;
  let workgroupIdx = wid.x;

  // Each thread loads 2 elements into shared memory
  let idx0 = globalIdx * 2u;
  let idx1 = globalIdx * 2u + 1u;

  temp[localIdx * 2u] = select(0u, atomicLoad(&cellCount[idx0]), idx0 < params.totalGridCells);
  temp[localIdx * 2u + 1u] = select(0u, atomicLoad(&cellCount[idx1]), idx1 < params.totalGridCells);

  workgroupBarrier();

  // Up-sweep phase (parallel reduction)
  var offset = 1u;
  for (var d = 256u; d > 0u; d >>= 1u) {
    workgroupBarrier();

    if (localIdx < d) {
      let ai = offset * (2u * localIdx + 1u) - 1u;
      let bi = offset * (2u * localIdx + 2u) - 1u;
      temp[bi] += temp[ai];
    }

    offset *= 2u;
  }

  // Store block sum and clear last element for down-sweep
  if (localIdx == 0u) {
    blockSums[workgroupIdx] = temp[511];
    temp[511] = 0u;
  }

  workgroupBarrier();

  // Down-sweep phase (distribute sums)
  for (var d = 1u; d < 512u; d *= 2u) {
    offset >>= 1u;
    workgroupBarrier();

    if (localIdx < d) {
      let ai = offset * (2u * localIdx + 1u) - 1u;
      let bi = offset * (2u * localIdx + 2u) - 1u;

      let t = temp[ai];
      temp[ai] = temp[bi];
      temp[bi] += t;
    }
  }

  workgroupBarrier();

  // Write results back to global memory
  if (idx0 < params.totalGridCells) {
    prefixSum[idx0] = temp[localIdx * 2u];
  }
  if (idx1 < params.totalGridCells) {
    prefixSum[idx1] = temp[localIdx * 2u + 1u];
  }
}
`;

/**
 * PASS 4b: Prefix Sum Blocks - Scan the block sums
 * This handles the per-block totals from the local scan
 */
export const GRID_PREFIX_SUM_BLOCKS = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

var<workgroup> blockTemp: array<u32, 512>;

@compute @workgroup_size(256)
fn prefixSumBlocks(
  @builtin(global_invocation_id) gid: vec3<u32>,
  @builtin(local_invocation_id) lid: vec3<u32>
) {
  let globalIdx = gid.x;
  let localIdx = lid.x;

  // Calculate number of blocks from first pass
  let numBlocks = (params.totalGridCells + 511u) / 512u;

  // Load block sums (2 per thread)
  let idx0 = globalIdx * 2u;
  let idx1 = globalIdx * 2u + 1u;

  blockTemp[localIdx * 2u] = select(0u, blockSums[idx0], idx0 < numBlocks);
  blockTemp[localIdx * 2u + 1u] = select(0u, blockSums[idx1], idx1 < numBlocks);

  workgroupBarrier();

  // Up-sweep
  var offset = 1u;
  for (var d = 256u; d > 0u; d >>= 1u) {
    workgroupBarrier();

    if (localIdx < d) {
      let ai = offset * (2u * localIdx + 1u) - 1u;
      let bi = offset * (2u * localIdx + 2u) - 1u;
      blockTemp[bi] += blockTemp[ai];
    }

    offset *= 2u;
  }

  if (localIdx == 0u) {
    blockTemp[511] = 0u;
  }

  workgroupBarrier();

  // Down-sweep
  for (var d = 1u; d < 512u; d *= 2u) {
    offset >>= 1u;
    workgroupBarrier();

    if (localIdx < d) {
      let ai = offset * (2u * localIdx + 1u) - 1u;
      let bi = offset * (2u * localIdx + 2u) - 1u;

      let t = blockTemp[ai];
      blockTemp[ai] = blockTemp[bi];
      blockTemp[bi] += t;
    }
  }

  workgroupBarrier();

  // Write scanned block sums back
  if (idx0 < numBlocks) {
    blockSums[idx0] = blockTemp[localIdx * 2u];
  }
  if (idx1 < numBlocks) {
    blockSums[idx1] = blockTemp[localIdx * 2u + 1u];
  }
}
`;

/**
 * PASS 4c: Prefix Sum Add - Add block sums back to prefix sums
 */
export const GRID_PREFIX_SUM_ADD = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn prefixSumAdd(
  @builtin(global_invocation_id) gid: vec3<u32>,
  @builtin(workgroup_id) wid: vec3<u32>
) {
  let idx = gid.x;
  if (idx >= params.totalGridCells) { return; }

  let workgroupIdx = wid.x;

  // Add this workgroup's block sum to all elements
  if (workgroupIdx > 0u) {
    prefixSum[idx] += blockSums[workgroupIdx];
  }
}
`;

/**
 * PASS 4d: Copy to Offset - Copy prefix sum to cellOffset for scatter phase
 * This preserves the prefix sum while allowing atomic writes during scatter
 */
export const GRID_COPY_TO_OFFSET = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn copyToOffset(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.totalGridCells) { return; }

  // Copy prefix sum to offset buffer (as atomic u32)
  atomicStore(&cellOffset[idx], prefixSum[idx]);
}
`;

/**
 * PASS 5: Scatter - Place particles into sorted array
 * Uses prefix sum as base offset, atomic increment for thread-safe writes
 */
export const GRID_SCATTER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${GRID_PARAMS_BINDINGS}
${GRID_BUFFER_BINDINGS}

@compute @workgroup_size(256)
fn scatterParticles(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.activeCount) { return; }

  // Get this particle's cell hash
  let hash = cellHashBuffer[idx];

  // Atomically get write position and increment
  let dest = atomicAdd(&cellOffset[hash], 1u);

  // Write particle index to sorted array
  sortedIndex[dest] = idx;
}
`;

/**
 * Neighbor Iteration Helper - Query particles in neighboring grid cells
 * Used by density, force, and solver shaders
 */
export const GRID_NEIGHBOR_HELPER = /* wgsl */`
// Get the range [start, end) of particles in a specific grid cell
// Returns (0, 0) for out-of-bounds cells
fn getNeighborRange(cellX: i32, cellY: i32, cellZ: i32, gridDim: vec3<u32>, totalCells: u32) -> vec2<u32> {
  // Bounds check
  if (cellX < 0 || cellY < 0 || cellZ < 0 ||
      cellX >= i32(gridDim.x) || cellY >= i32(gridDim.y) || cellZ >= i32(gridDim.z)) {
    return vec2<u32>(0u, 0u);
  }

  // Compute cell hash
  let hash = u32(cellX) + u32(cellY) * gridDim.x + u32(cellZ) * gridDim.x * gridDim.y;

  // Range is [prefixSum[hash], prefixSum[hash+1])
  let start = prefixSum[hash];
  let end = select(prefixSum[hash + 1u], params.activeCount, hash + 1u >= totalCells);

  return vec2<u32>(start, end);
}

// Iterate over all 27 neighboring cells (3x3x3 stencil)
// Call this with a callback/loop to process all neighbors
fn forEachNeighborCell(centerCell: vec3<i32>, gridDim: vec3<u32>, totalCells: u32) -> array<vec2<u32>, 27> {
  var ranges: array<vec2<u32>, 27>;
  var idx = 0u;

  for (var dz = -1; dz <= 1; dz++) {
    for (var dy = -1; dy <= 1; dy++) {
      for (var dx = -1; dx <= 1; dx++) {
        let cellX = centerCell.x + dx;
        let cellY = centerCell.y + dy;
        let cellZ = centerCell.z + dz;

        ranges[idx] = getNeighborRange(cellX, cellY, cellZ, gridDim, totalCells);
        idx++;
      }
    }
  }

  return ranges;
}

// Simplified version: get particle index from sortedIndex buffer
fn getSortedParticleIndex(sortedIdx: u32) -> u32 {
  return sortedIndex[sortedIdx];
}
`;

/**
 * Build complete grid shader with all passes
 * Useful for debugging or single-module compilation
 */
export function buildGridShader(): string {
  return buildShader(
    '// === SPATIAL HASH GRID SHADERS ===',
    '',
    '// Pass 1: Hash Particles',
    GRID_HASH_PARTICLES,
    '',
    '// Pass 2: Clear Counts',
    GRID_CLEAR_COUNTS,
    '',
    '// Pass 3: Count Particles',
    GRID_COUNT_PARTICLES,
    '',
    '// Pass 4a: Local Prefix Sum',
    GRID_PREFIX_SUM_LOCAL,
    '',
    '// Pass 4b: Block Prefix Sum',
    GRID_PREFIX_SUM_BLOCKS,
    '',
    '// Pass 4c: Add Block Sums',
    GRID_PREFIX_SUM_ADD,
    '',
    '// Pass 4d: Copy to Offset',
    GRID_COPY_TO_OFFSET,
    '',
    '// Pass 5: Scatter Particles',
    GRID_SCATTER,
    '',
    '// Neighbor Query Helper',
    GRID_NEIGHBOR_HELPER,
  );
}

/**
 * Grid shader metadata for pipeline creation
 */
export const GRID_SHADER_METADATA = {
  passes: [
    { name: 'hashParticles', entryPoint: 'hashParticles', workgroupSize: 256 },
    { name: 'clearCounts', entryPoint: 'clearCounts', workgroupSize: 256 },
    { name: 'countParticles', entryPoint: 'countParticles', workgroupSize: 256 },
    { name: 'prefixSumLocal', entryPoint: 'prefixSumLocal', workgroupSize: 256 },
    { name: 'prefixSumBlocks', entryPoint: 'prefixSumBlocks', workgroupSize: 256 },
    { name: 'prefixSumAdd', entryPoint: 'prefixSumAdd', workgroupSize: 256 },
    { name: 'copyToOffset', entryPoint: 'copyToOffset', workgroupSize: 256 },
    { name: 'scatterParticles', entryPoint: 'scatterParticles', workgroupSize: 256 },
  ],

  bufferBindings: {
    group0: ['params', 'positionMass', 'velocityPressure'],
    group2: ['cellCount', 'prefixSum', 'sortedIndex', 'cellHashBuffer', 'cellOffset', 'blockSums'],
  },

  // Performance notes
  complexity: {
    hash: 'O(n)',
    clear: 'O(m) where m = total grid cells',
    count: 'O(n)',
    prefixSum: 'O(m) with parallel scan',
    scatter: 'O(n)',
    total: 'O(n + m)',
  },

  // Grid size recommendations
  recommendations: {
    minCellSize: '2.0 * smoothingRadius',
    maxGridCells: 2097152, // 128^3, practical limit for single-level prefix sum
    typicalGridDim: [32, 64, 128],
  },
};
